/**
 * ProposedStoryCard
 *
 * One story from a breakdown proposal: title, description, acceptance
 * criteria, affected areas, its estimate, and the approve/reject toggle.
 *
 * Approved is the resting state — a story is included unless turned down —
 * so rejection is what changes the rendering: struck-through title, muted
 * body. The card stays visible either way; the work item's acceptance
 * criteria require rejected stories to remain visible until committed.
 */

import { Component, For, Show } from "solid-js";
import type { ProposedStory } from "../../api/types";
import { EstimateChip } from "./EstimateChip";

export interface ProposedStoryCardProps {
  story: ProposedStory;
  rejected: boolean;
  onToggle: (id: string) => void;
}

export const ProposedStoryCard: Component<ProposedStoryCardProps> = (props) => {
  const toggleLabel = () =>
    props.rejected
      ? `Restore "${props.story.title}" — currently rejected`
      : `Reject "${props.story.title}"`;

  return (
    <div
      class={
        "rounded-md border p-4 " +
        (props.rejected ? "border-line bg-sunken" : "border-line bg-surface")
      }
    >
      <div class="flex items-start justify-between gap-4">
        <h3
          class={
            "text-sm font-semibold " +
            (props.rejected ? "text-ink-3 line-through" : "text-ink")
          }
        >
          {props.story.title}
        </h3>

        <button
          type="button"
          aria-pressed={props.rejected}
          aria-label={toggleLabel()}
          onClick={() => props.onToggle(props.story.id)}
          class={
            "shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium " +
            "focus-ring " +
            (props.rejected
              ? "bg-signal-red-tint text-signal-red hover:bg-signal-red/15"
              : "bg-signal-green-tint text-signal-green hover:bg-signal-green/15")
          }
        >
          <Show when={props.rejected} fallback="Approved">
            Rejected
          </Show>
        </button>
      </div>

      <p
        class={
          "mt-2 text-sm whitespace-pre-wrap " +
          (props.rejected ? "text-ink-3" : "text-ink-2")
        }
      >
        {props.story.description}
      </p>

      <Show when={props.story.acceptance_criteria.length > 0}>
        <ul class="mt-3 flex flex-col gap-1">
          <For each={props.story.acceptance_criteria}>
            {(criterion) => (
              <li
                class={
                  "text-sm flex gap-2 " +
                  (props.rejected ? "text-ink-3" : "text-ink-2")
                }
              >
                <span class="text-ink-3" aria-hidden="true">
                  •
                </span>
                {criterion}
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show when={props.story.affected_areas.length > 0}>
        <ul class="mt-3 flex flex-col gap-1">
          <For each={props.story.affected_areas}>
            {(area) => (
              <li class="text-xs">
                <code
                  class={
                    "rounded px-1 py-0.5 font-mono " +
                    (props.rejected
                      ? "bg-surface text-ink-3"
                      : "bg-sunken text-ink-2")
                  }
                >
                  {area.path}
                </code>
                <span class="ml-1.5 text-ink-3">{area.reason}</span>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <div class="mt-3">
        <EstimateChip
          estimate={props.story.estimate}
          storyTitle={props.story.title}
        />
      </div>
    </div>
  );
};

export default ProposedStoryCard;
